"use client";

import {
  createContext,
  useContext,
  useCallback,
  useSyncExternalStore,
  type ReactNode,
} from "react";

export type ViewMode = "admin" | "user";

interface ViewModeContextValue {
  mode: ViewMode;
  setMode: (mode: ViewMode) => void;
  isUserMode: boolean;
}

const ViewModeContext = createContext<ViewModeContextValue | null>(null);

const STORAGE_KEY = "xd_view_mode";

const VIEWMODE_EVENT = "xd:viewmode-changed";

// Read the persisted mode as an external store. useSyncExternalStore drives the
// hydration render from getServerSnapshot ("admin") and only switches to the
// stored value AFTER hydration, so the server HTML and the client's first paint
// always agree. Reading localStorage in a useState initializer instead would
// desync them — SSR yields "admin" while the client yields the stored mode —
// flipping isUserMode and every permission-gated element (e.g. the sidebar nav
// groups), which React reports as a hydration mismatch.
function subscribe(callback: () => void): () => void {
  window.addEventListener(VIEWMODE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(VIEWMODE_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}

function getSnapshot(): ViewMode {
  return localStorage.getItem(STORAGE_KEY) === "user" ? "user" : "admin";
}

function getServerSnapshot(): ViewMode {
  return "admin";
}

export function ViewModeProvider({ children }: { children: ReactNode }) {
  const mode = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setMode = useCallback((m: ViewMode) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, m);
      (window as unknown as Record<string, string>).__xd_view_mode = m;
      // Notify this tab's subscribers (storage events only fire cross-tab) so
      // useSyncExternalStore re-reads, then force queries to refetch against the
      // new permission context.
      window.dispatchEvent(new CustomEvent(VIEWMODE_EVENT));
    }
  }, []);

  return (
    <ViewModeContext.Provider
      value={{ mode, setMode, isUserMode: mode === "user" }}
    >
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode(): ViewModeContextValue {
  const ctx = useContext(ViewModeContext);
  if (!ctx) {
    return { mode: "admin", setMode: () => {}, isUserMode: false };
  }
  return ctx;
}

/**
 * Read the current view mode synchronously (for the API client which
 * runs outside React context).
 */
export function getViewMode(): ViewMode {
  if (typeof window !== "undefined") {
    const win = (window as unknown as Record<string, string>).__xd_view_mode as ViewMode | undefined;
    if (win) return win;
    // Fallback to localStorage before context mounts
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "user" ? "user" : "admin";
  }
  return "admin";
}
